/**
 * @ngdoc controller
 * @name CampaignController
 * @description
 * This component renders the Campaign section.
 *
 * @memberof campaigns
 */
class CampaignController {
  /**
   * Class constructor
   * @param {Service} $location        To redirect the user when the save process finishes.
   * @param {Object}  appConstant      The app configuration dictionary.
   * @param {Service} campaignsService To get the customer campaigns by id.
   * @param {Service} customerService  To get the customer information.
   * @param {Service} errorHandler     To show any errors.
   * @ignore
   */
  constructor(
    $location,
    appConstant,
    campaignsService,
    customerService,
    errorHandler,
  ) {
    'ngInject';

    /**
     * The local reference to the `$location` service.
     * @type {Object}
     */
    this.$location = $location;
    /**
     * The local reference to the `campaignsService` service.
     * @type {Object}
     */
    this.campaignsService = campaignsService;
    /**
     * The local reference to the `customerService` service.
     * @type {Object}
     */
    this.customerService = customerService;
    /**
     * The local reference to the `errorHandler` service.
     * @type {Object}
     */
    this.errorHandler = errorHandler;
    /**
     * The local reference to the `campaign` object.
     * @type {Object}
     */
    this.campaign = campaignsService.getDefaultCampaignForEdit();
    /**
     * The local reference to the `customer` object.
     * @type {?Object}
     */
    this.customer = null;
    /**
     * If the tooltip in the `name` input should be shown.
     * @type {Boolean}
     */
    this.showTooltip = false;
    /**
     * If a request is in progress.
     * @type {Boolean}
     */
    this.isWorking = false;
    /**
     * The local reference to the `maxValues` object.
     * @type {Object}
     */
    this.maxValues = appConstant.maxValues.campaigns;
    /**
     * Local reference if the Delete Campaign should be shown or not.
     * @type {Boolean}
     */
    this.isDeleteCampaignVisible = false;
    /**
     * Local reference to the `deleteCampaignButtons` array.
     * These are the buttons the Delete Campaign modal should display.
     * @type Array<{title: String, class: String, action: Function}>
     */
    this.deleteCampaignButtons = [{
      title: 'Cancel',
      class: '-secondary',
      action: this.hideDeleteCampaignModal.bind(this),
    }, {
      title: 'Delete',
      class: '-destructive',
      action: this.deleteCampaign.bind(this),
    }];
    /**
     * Local reference to the Campaign we are gonna delete.
     * @type {?Object}
     */
    this.campaignToDelete = null;
  }
  /**
   * Load the campaign by id if available and the customer information.
   */
  $onInit() {
    if (this.campaignId) {
      this.campaignsService.getCampaignById(this.campaignId)
      .then((campaign) => {
        this.campaign = campaign.processForEdit();
        this.campaignToDelete = angular.copy(this.campaign);
      })
      .catch((err) => this.errorHandler.handleError(err));
    } else {
      // Fetch all the campaigns.
      this.campaignsService.getCampaigns();
    }

    this.customerService.getCustomer()
    .then((customer) => {
      this.customer = customer;
    })
    .catch((err) => this.errorHandler.handleError(err));
  }
  /**
   * Add a guideline to the negative list.
   */
  addGuidelineNegative() {
    this.campaign.guidelines.negative.push({ value: '' });
  }
  /**
   * Add a guideline to the positive list.
   */
  addGuidelinePositive() {
    this.campaign.guidelines.positive.push({ value: '' });
  }
  /**
   * Handle the delete campaign action. Once the delete is done
   * the user will be redirected to the campaigns list.
   */
  deleteCampaign() {
    this.campaignsService.deleteCampaign(this.campaignToDelete)
    .then(() => {
      this.$location.url('/campaigns');
    })
    .catch((err) => this.errorHandler.handleError(err));
  }
  /**
   * Set a new file as campaign cover image.
   * @param {FileList} selectedFile seleted file by the user.
   */
  fileSelected([selectedFile]) {
    this.isWorking = true;
    this.campaignsService.uploadCoverImage(selectedFile)
    .then((changedCampaign) => {
      angular.merge(this.campaign, changedCampaign);
    })
    .finally(() => {
      this.isWorking = false;
    });
  }
  /**
   * Handle the hide of Delete Campaign modal.
   */
  hideDeleteCampaignModal() {
    this.isDeleteCampaignVisible = false;
  }
  /**
   * Remove a guideline form the negative list.
   * @param {Number} index the guideline index to remove.
   */
  removeGuidelineNegative(index) {
    this.campaign.guidelines.negative.splice(index, 1);
  }
  /**
   * Remove a guideline form the postive list.
   * @param {Number} index the guideline index to remove.
   */
  removeGuidelinePositive(index) {
    this.campaign.guidelines.positive.splice(index, 1);
  }
  /**
   * Handle the save campaign action.
   */
  saveCampaign() {
    if (this.campaignsService.validateCampaign(this.campaign)) {
      this.isWorking = true;
      this.campaignsService.saveCampaign(this.campaign)
      .then(() => {
        this.$location.url('/campaigns');
      })
      .catch((err) => this.errorHandler.handleError(err))
      .finally(() => {
        this.isWorking = false;
      });
    }
  }
  /**
   * Show or hide the tooltip in the `name` input.
   * @param {Boolean} show if the tooltip should be shown.
   */
  setShowTooltip(show) {
    this.showTooltip = show;
  }
  /**
   * Handle the show of Delete Campaign modal.
   */
  showDeleteCampaignModal() {
    this.isDeleteCampaignVisible = true;
  }
}

export default CampaignController;



// WEBPACK FOOTER //
// ./src/frontend/modules/campaigns/components/campaign/campaign.controller.js